import React, { useRef, useEffect, useState } from 'react';
import { FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify } from 'react-icons/fa';
import './RichTextEditor.css';

const RichTextEditor = ({ value, onChange, placeholder = 'Enter text...', label }) => {
  const editorRef = useRef(null);
  const [activeFormat, setActiveFormat] = useState({ bold: false, italic: false, underline: false });
  const [currentSize, setCurrentSize] = useState('');
  const [currentFontFamily, setCurrentFontFamily] = useState('');
  const [currentFontWeight, setCurrentFontWeight] = useState('');

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  useEffect(() => {
    // Update active format state when selection changes
    const updateFormatState = () => {
      if (editorRef.current && document.activeElement === editorRef.current) {
        setActiveFormat({
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          underline: document.queryCommandState('underline')
        });
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('mouseup', updateFormatState);
      editor.addEventListener('keyup', updateFormatState);
      return () => {
        editor.removeEventListener('mouseup', updateFormatState);
        editor.removeEventListener('keyup', updateFormatState);
      };
    }
  }, []);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const getSelection = () => {
    return window.getSelection();
  };

  const getSelectedRange = () => {
    const selection = getSelection();
    if (selection.rangeCount > 0) {
      return selection.getRangeAt(0);
    }
    return null;
  };

  const execCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
    // Update format state after command
    setTimeout(() => {
      setActiveFormat({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline')
      });
    }, 10);
  };

  const applyColorToSelection = (color, isBackground = false) => {
    editorRef.current?.focus();
    const range = getSelectedRange();
    
    if (range && !range.collapsed) {
      // We have a selection - wrap it in a span
      const span = document.createElement('span');
      if (isBackground) {
        span.style.backgroundColor = color;
      } else {
        span.style.color = color;
      }
      
      try {
        range.surroundContents(span);
        handleInput();
        // Restore selection
        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        const selection = getSelection();
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        // If surroundContents fails, extract content and replace
        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);
        handleInput();
      }
    } else {
      // No selection - use execCommand (will apply to next typed text)
      if (isBackground) {
        execCommand('backColor', color);
      } else {
        execCommand('foreColor', color);
      }
    }
  };

  const applyFontSize = (size) => {
    if (!size) return;
    
    editorRef.current?.focus();
    const range = getSelectedRange();
    
    // Map size values to actual font sizes - Expanded for luxe design
    const sizeMap = {
      '1': '10px',
      '2': '12px',
      '3': '14px',
      '4': '16px',
      '5': '18px',
      '6': '20px',
      '7': '24px',
      '8': '28px',
      '9': '32px',
      '10': '36px',
      '11': '40px',
      '12': '48px',
      '13': '56px',
      '14': '60px',
      '15': '64px',
      '16': '72px'
    };
    
    const fontSize = sizeMap[size] || '14px';
    
    if (range && !range.collapsed) {
      // We have a selection - wrap it in a span with font size
      const span = document.createElement('span');
      span.style.fontSize = fontSize;
      
      try {
        range.surroundContents(span);
        handleInput();
        setCurrentSize('');
        // Restore selection
        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        const selection = getSelection();
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        // If surroundContents fails, extract content and replace
        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);
        handleInput();
        setCurrentSize('');
      }
    } else {
      // No selection - use execCommand
      execCommand('fontSize', size);
      setCurrentSize('');
    }
  };

  const applyFontFamily = (fontFamily) => {
    if (!fontFamily) return;
    
    editorRef.current?.focus();
    const range = getSelectedRange();
    
    const fontMap = {
      'serif': "'Cormorant Garamond', 'Georgia', serif",
      'sans-serif': "'Inter', system-ui, sans-serif",
      'heading': "'Cormorant Garamond', 'Georgia', serif",
      'body': "'Inter', system-ui, sans-serif"
    };
    
    const fontValue = fontMap[fontFamily] || fontFamily;
    
    if (range && !range.collapsed) {
      const span = document.createElement('span');
      span.style.fontFamily = fontValue;
      
      try {
        range.surroundContents(span);
        handleInput();
        setCurrentFontFamily('');
        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        const selection = getSelection();
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);
        handleInput();
        setCurrentFontFamily('');
      }
    } else {
      execCommand('fontName', fontValue);
      setCurrentFontFamily('');
    }
  };

  const applyFontWeight = (weight) => {
    if (!weight) return;
    
    editorRef.current?.focus();
    const range = getSelectedRange();
    
    const weightMap = {
      'light': '300',
      'normal': '400',
      'medium': '500',
      'semibold': '600',
      'bold': '700'
    };
    
    const fontWeight = weightMap[weight] || weight;
    
    if (range && !range.collapsed) {
      const span = document.createElement('span');
      span.style.fontWeight = fontWeight;
      
      try {
        range.surroundContents(span);
        handleInput();
        setCurrentFontWeight('');
        const newRange = document.createRange();
        newRange.selectNodeContents(span);
        const selection = getSelection();
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        const contents = range.extractContents();
        span.appendChild(contents);
        range.insertNode(span);
        handleInput();
        setCurrentFontWeight('');
      }
    } else {
      const span = document.createElement('span');
      span.style.fontWeight = fontWeight;
      execCommand('insertHTML', span.outerHTML);
      setCurrentFontWeight('');
    }
  };

  const isCommandActive = (command) => {
    if (command === 'bold') return activeFormat.bold;
    if (command === 'italic') return activeFormat.italic;
    if (command === 'underline') return activeFormat.underline;
    return document.queryCommandState(command);
  };

  return (
    <div className="rich-text-editor">
      {label && <label className="rich-text-editor-label">{label}</label>}
      
      {/* Toolbar */}
      <div className="rich-text-toolbar">
        {/* Text Formatting */}
        <div className="toolbar-group">
          <button
            type="button"
            className={`toolbar-btn ${isCommandActive('bold') ? 'active' : ''}`}
            onClick={() => execCommand('bold')}
            title="Bold"
          >
            <FaBold />
          </button>
          <button
            type="button"
            className={`toolbar-btn ${isCommandActive('italic') ? 'active' : ''}`}
            onClick={() => execCommand('italic')}
            title="Italic"
          >
            <FaItalic />
          </button>
          <button
            type="button"
            className={`toolbar-btn ${isCommandActive('underline') ? 'active' : ''}`}
            onClick={() => execCommand('underline')}
            title="Underline"
          >
            <FaUnderline />
          </button>
        </div>

        {/* Font Size */}
        <div className="toolbar-group">
          <select
            className="toolbar-select"
            value={currentSize}
            onChange={(e) => {
              const size = e.target.value;
              applyFontSize(size);
            }}
            onClick={(e) => {
              // Reset to empty to allow reselection
              if (e.target.value) {
                setTimeout(() => {
                  e.target.value = '';
                  setCurrentSize('');
                }, 100);
              }
            }}
            title="Font Size - Select text first, then choose size"
          >
            <option value="">Size</option>
            <optgroup label="Small">
              <option value="1">10px</option>
              <option value="2">12px</option>
              <option value="3">14px</option>
              <option value="4">16px</option>
            </optgroup>
            <optgroup label="Medium">
              <option value="5">18px</option>
              <option value="6">20px</option>
              <option value="7">24px</option>
              <option value="8">28px</option>
            </optgroup>
            <optgroup label="Large">
              <option value="9">32px</option>
              <option value="10">36px</option>
              <option value="11">40px</option>
              <option value="12">48px</option>
            </optgroup>
            <optgroup label="Hero Banner Sizes (Luxe)">
              <option value="13">56px</option>
              <option value="14">60px</option>
              <option value="15">64px</option>
              <option value="16">72px</option>
            </optgroup>
          </select>
        </div>

        {/* Font Family */}
        <div className="toolbar-group">
          <select
            className="toolbar-select"
            value={currentFontFamily}
            onChange={(e) => {
              const fontFamily = e.target.value;
              applyFontFamily(fontFamily);
            }}
            onClick={(e) => {
              if (e.target.value) {
                setTimeout(() => {
                  e.target.value = '';
                  setCurrentFontFamily('');
                }, 100);
              }
            }}
            title="Font Family - Select text first, then choose font"
          >
            <option value="">Font</option>
            <option value="serif">Serif (Cormorant Garamond) - Luxury</option>
            <option value="sans-serif">Sans-Serif (Inter) - Modern</option>
          </select>
        </div>

        {/* Font Weight */}
        <div className="toolbar-group">
          <select
            className="toolbar-select"
            value={currentFontWeight}
            onChange={(e) => {
              const weight = e.target.value;
              applyFontWeight(weight);
            }}
            onClick={(e) => {
              if (e.target.value) {
                setTimeout(() => {
                  e.target.value = '';
                  setCurrentFontWeight('');
                }, 100);
              }
            }}
            title="Font Weight - Select text first, then choose weight"
          >
            <option value="">Weight</option>
            <option value="light">Light (300) - Elegant</option>
            <option value="normal">Normal (400)</option>
            <option value="medium">Medium (500)</option>
            <option value="semibold">Semibold (600)</option>
            <option value="bold">Bold (700)</option>
          </select>
        </div>

        {/* Text Color */}
        <div className="toolbar-group">
          <input
            type="color"
            className="toolbar-color"
            onChange={(e) => applyColorToSelection(e.target.value, false)}
            onClick={(e) => {
              // Ensure editor has focus
              editorRef.current?.focus();
            }}
            title="Text Color - Select text first, then choose color"
          />
          <span className="toolbar-label">Text</span>
        </div>

        {/* Background Color */}
        <div className="toolbar-group">
          <input
            type="color"
            className="toolbar-color"
            onChange={(e) => applyColorToSelection(e.target.value, true)}
            onClick={(e) => {
              // Ensure editor has focus
              editorRef.current?.focus();
            }}
            title="Background Color - Select text first, then choose color"
          />
          <span className="toolbar-label">BG</span>
        </div>

        {/* Text Alignment */}
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('justifyLeft')}
            title="Align Left"
          >
            <FaAlignLeft />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('justifyCenter')}
            title="Align Center"
          >
            <FaAlignCenter />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('justifyRight')}
            title="Align Right"
          >
            <FaAlignRight />
          </button>
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('justifyFull')}
            title="Justify"
          >
            <FaAlignJustify />
          </button>
        </div>

        {/* Remove Formatting */}
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand('removeFormat')}
            title="Remove Formatting"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        className="rich-text-content"
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        onMouseUp={() => {
          // Update format state on mouse up
          setActiveFormat({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline')
          });
        }}
        onKeyUp={() => {
          // Update format state on key up
          setActiveFormat({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline')
          });
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
      <small style={{ display: 'block', marginTop: '8px', color: '#666', fontSize: '11px', fontStyle: 'italic' }}>
        💡 Tip: Select text first, then apply formatting (color, size, bold, etc.)
      </small>
    </div>
  );
};

export default RichTextEditor;

